<template>
  <div>
    <div class="row g-3 mb-4">
      <div v-for="statistic in statisticCards" :key="statistic.label" class="col-12 col-sm-6 col-xl-3">
        <div class="card inventory-stat-card">
          <div class="card-body d-flex justify-content-between align-items-center">
            <div>
              <small class="text-muted d-block mb-1">{{$t(statistic.label)}}</small>
              <h4 class="mb-0" :class="statistic.valueClass">
                {{statistic.money ? formatMoney(statistic.value) : formatNumber(statistic.value)}}
              </h4>
            </div>
            <div class="inventory-stat-icon" :class="statistic.iconClass">
              <AppIcon :name="statistic.icon" class="bi" :class="statistic.icon" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header bg-white">
        <h6 class="mb-0"><AppIcon name="bi-clipboard2-pulse" class="bi bi-clipboard2-pulse me-2" />{{$t('inventory_status')}}</h6>
      </div>
      <div class="table-responsive">
        <table class="table mb-0 align-middle">
          <thead>
            <tr class="text-secondary">
              <th class="px-4 py-3">{{$t('product')}}</th>
              <th class="px-4 py-3">{{$t('reference')}}</th>
              <th class="px-4 py-3 text-end">{{$t('quantity')}}</th>
              <th class="px-4 py-3 text-end">{{$t('min_quantity')}}</th>
              <th class="px-4 py-3">{{$t('status')}}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inventory in stockAlerts" :key="inventory.id">
              <td class="px-4 py-3 fw-semibold">{{inventory.product ? inventory.product.label : '-'}}</td>
              <td class="px-4 py-3">{{inventory.product && inventory.product.reference ? inventory.product.reference : (inventory.reference || '-')}}</td>
              <td class="px-4 py-3 text-end">{{formatNumber(inventory.quantity)}}</td>
              <td class="px-4 py-3 text-end">{{formatNumber(inventory.min_quantity)}}</td>
              <td class="px-4 py-3">
                <span v-if="toNumber(inventory.quantity) <= 0" class="badge bg-danger">{{$t('out_of_stock')}}</span>
                <span v-else class="badge bg-warning text-dark">{{$t('low_stock_products')}}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="!stockAlerts.length" class="card-body text-center py-5">
        <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success d-block mb-2" style="font-size: 2rem" />
        <span class="text-muted">{{$t('no_data')}}</span>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../helper'

export default {
  name: 'InventoryStatistics',
  props: {
    inventories: {
      type: Array,
      default: () => []
    },
    totalProducts: {
      type: Number,
      default: null
    }
  },
  computed: {
    statistics() {
      const statistics = this.inventories.reduce((totals, inventory) => {
        const quantity = this.toNumber(inventory.quantity)
        const positiveQuantity = Math.max(quantity, 0)
        const minimumQuantity = this.toNumber(inventory.min_quantity)
        const purchasePrice = this.toNumber(inventory.purchase_price)
        const sellingPrice = this.toNumber(inventory.price)
        const hasPurchasePrice = inventory.purchase_price !== null && inventory.purchase_price !== '' && Number.isFinite(Number(inventory.purchase_price))
        const hasSellingPrice = inventory.price !== null && inventory.price !== '' && Number.isFinite(Number(inventory.price))

        totals.totalQuantity += positiveQuantity
        totals.stockValue += positiveQuantity * purchasePrice
        totals.potentialSellingValue += positiveQuantity * sellingPrice

        if (hasPurchasePrice && hasSellingPrice) {
          totals.potentialProfit += positiveQuantity * (sellingPrice - purchasePrice)
        }

        if (quantity > 0) totals.inStockProducts += 1
        if (quantity > 0 && quantity < minimumQuantity) totals.lowStockProducts += 1
        if (quantity <= 0) totals.outOfStockProducts += 1

        return totals
      }, {
        totalQuantity: 0,
        inStockProducts: 0,
        lowStockProducts: 0,
        outOfStockProducts: 0,
        stockValue: 0,
        potentialSellingValue: 0,
        potentialProfit: 0
      })

      statistics.totalProducts = this.totalProducts === null ? this.inventories.length : this.totalProducts
      return statistics
    },
    statisticCards() {
      return [
        { label: 'total_products', value: this.statistics.totalProducts, icon: 'bi-box-seam', iconClass: 'bg-primary-subtle text-primary' },
        { label: 'quantity', value: this.statistics.totalQuantity, icon: 'bi-boxes', iconClass: 'bg-info-subtle text-info' },
        { label: 'in_stock_products', value: this.statistics.inStockProducts, icon: 'bi-check-circle', iconClass: 'bg-success-subtle text-success', valueClass: 'text-success' },
        { label: 'low_stock_products', value: this.statistics.lowStockProducts, icon: 'bi-exclamation-triangle', iconClass: 'bg-warning-subtle text-warning', valueClass: 'text-warning' },
        { label: 'out_of_stock_products', value: this.statistics.outOfStockProducts, icon: 'bi-x-octagon', iconClass: 'bg-danger-subtle text-danger', valueClass: 'text-danger' },
        { label: 'stock_value', value: this.statistics.stockValue, icon: 'bi-cash-stack', iconClass: 'bg-success-subtle text-success', money: true },
        { label: 'potential_selling_value', value: this.statistics.potentialSellingValue, icon: 'bi-currency-exchange', iconClass: 'bg-primary-subtle text-primary', money: true },
        { label: 'potential_profit_in_stock', value: this.statistics.potentialProfit, icon: 'bi-graph-up-arrow', iconClass: 'bg-info-subtle text-info', money: true }
      ]
    },
    stockAlerts() {
      return this.inventories
        .filter(inventory => {
          const quantity = this.toNumber(inventory.quantity)
          return quantity <= 0 || quantity < this.toNumber(inventory.min_quantity)
        })
        .sort((first, second) => this.toNumber(first.quantity) - this.toNumber(second.quantity))
    }
  },
  methods: {
    formatMoney(number) {
      return helper.formatMoney(number)
    },
    formatNumber(number) {
      return new Intl.NumberFormat(this.$i18n.locale, { maximumFractionDigits: 2 }).format(this.toNumber(number))
    },
    toNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }
  }
}
</script>

<style scoped>
.inventory-stat-card {
  height: 100%;
}

.inventory-stat-icon {
  align-items: center;
  border-radius: 12px;
  display: inline-flex;
  flex: 0 0 46px;
  font-size: 22px;
  height: 46px;
  justify-content: center;
  width: 46px;
}

.bg-primary-subtle { background-color: rgba(13, 110, 253, .12); }
.bg-success-subtle { background-color: rgba(25, 135, 84, .12); }
.bg-warning-subtle { background-color: rgba(255, 193, 7, .18); }
.bg-danger-subtle { background-color: rgba(220, 53, 69, .12); }
.bg-info-subtle { background-color: rgba(13, 202, 240, .12); }
</style>
